<template>
  <div
    class="flex w-full bg-white h-screen overflow-y-auto items-start make-items-center"
  >
    <!-- From Column -->
    <div
      class="w-full sm:w-full lg:w-1/2 items-center flex justify-center p-8 md:p-16 h-full select-none"
    >
      <div
        class="max-w-md mx-auto w-full space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700"
      >
        <div
          class="flex flex-col sm:flex-row items-center justify-center md:justify-between"
        >
          <img :src="logo.url" class="h-9 w-auto" v-if="logo" />
          <span
            class="border-transparent h-4 w-0 border-r sm:h-10 sm:border-brand-ash mx-3"
          ></span>
          <span
            class="text-base md:text-xl font-semibold text-black tracking-tight self-center"
          >
            <template v-if="mode === 'conflict'">Switch accounts?</template>
            <template v-else>Welcome back</template>
          </span>
        </div>

        <div class="w-full space-y-6">
          <!-- Loading State -->
          <template v-if="loading">
            <div
              class="bg-gray-50 border border-gray-100 rounded-2xl p-12 text-center space-y-6 shadow-sm"
            >
              <Spinner class="mx-auto" />
              <p class="text-brand-secondary font-medium animate-pulse">
                Verifying magic link...
              </p>
            </div>
          </template>

          <!-- Content State -->
          <template v-else>
            <div
              class="bg-gray-50 border border-gray-100 rounded-2xl p-8 text-center space-y-6 shadow-sm relative overflow-hidden"
            >
              <div
                class="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-brand-primary opacity-5 rounded-full blur-2xl"
              ></div>

              <!-- Icon -->
              <div class="flex justify-center">
                <div
                  class="w-16 h-16 bg-brand-primary bg-opacity-10 rounded-full flex items-center justify-center text-brand-primary"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>

              <!-- Text Content -->
              <div class="space-y-4">
                <template v-if="mode === 'conflict'">
                  <p class="text-brand-secondary text-sm leading-relaxed">
                    You are currently logged in as
                    <span class="font-semibold text-black">{{
                      sessionEmail
                    }}</span
                    >.
                  </p>
                  <p class="text-brand-primary font-medium text-lg">
                    Switch to <span class="font-bold">{{ linkEmail }}</span
                    >?
                  </p>
                </template>

                <template v-else>
                  <p class="text-brand-primary font-bold text-xl">
                    Ready to sign in?
                  </p>
                  <p class="text-brand-secondary text-sm">
                    Click below to confirm and access your account.
                  </p>
                </template>
              </div>
            </div>

            <!-- Optional Password Field -->
            <div
              v-if="showPasswordField && mode !== 'conflict'"
              class="space-y-2 text-left"
            >
              <label class="text-sm font-medium text-brand-body ml-1"
                >For faster access, set a password (Optional)</label
              >
              <Input
                v-model="newPassword"
                type="password"
                placeholder="Create a password"
                class="w-full"
              />
            </div>

            <!-- Actions -->
            <div class="flex flex-col gap-3">
              <template v-if="mode === 'conflict'">
                <Button
                  title="Yes, Switch Accounts"
                  type="button"
                  :loader="btnLoading"
                  @click="switchAccounts"
                  class="w-full !bg-brand-primary !text-white hover:bg-opacity-90 transition-all font-semibold shadow-md"
                />
                <Button
                  title="Cancel"
                  type="button"
                  variant="secondary"
                  :disabled="btnLoading"
                  @click="cancel"
                  class="w-full !bg-white !text-brand-body border border-brand-ash hover:border-brand-primary transition-colors font-medium"
                />
              </template>

              <template v-else>
                <Button
                  title="Log In Now"
                  type="button"
                  :loader="btnLoading"
                  @click="confirmLogin"
                  class="w-full !bg-brand-primary !text-white hover:bg-opacity-90 transition-all font-semibold shadow-md"
                />
              </template>
            </div>

            <p
              v-if="errorMsg"
              class="text-red-500 text-sm font-medium text-center bg-red-50 p-3 rounded-lg border border-red-100"
            >
              {{ errorMsg }}
            </p>
          </template>
        </div>
      </div>
    </div>

    <!-- Info Column -->
    <InfoColumn />
  </div>
</template>

<script setup>
import { ref, onMounted, inject, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import Auth from "@/modules/Auth";
import InfoColumn from "../common/InfoColumn";
import axios1 from "axios";

const { isLoggedIn } = Auth();

const route = useRoute();
const router = useRouter();
const axios = inject("axios");

const loading = ref(true);
const btnLoading = ref(false);
const errorMsg = ref("");

const mode = ref("standard"); // standard | conflict
const linkEmail = ref("");
const sessionEmail = ref("");

const publicId = route.params.public_id;
const token = route.query.token || "";
const alreadyLoggedIn = ref(false);

// Password Field Logic
const showPasswordField = ref(false);
const newPassword = ref("");

const logo = ref("");
onMounted(() => {
  axios1.get(process.env.BASE_URL_API + "/logo/get").then((res) => {
    logo.value = res.data;
  });
});

const goError = () =>
  router.replace({
    name: "ExpiredMagicLink",
    query: { email: route.query.email },
  });

const loadPreview = async () => {
  if (!publicId || !token) {
    goError();
    return;
  }

  try {
    const res = await axios.api.get(`/auth/magic-link/preview/${publicId}`);
    if (!res?.data?.status) {
      goError();
      return;
    }

    const data = res.data.data || {};
    showPasswordField.value = data.pass_check;
    if (data.status === "expired" || data.status === "consumed") {
      goError();
      return;
    }

    sessionEmail.value = (localStorage.getItem("email") || "").trim();
    linkEmail.value = (route.query.email || "").trim();

    if (isLoggedIn()) {
      if (
        sessionEmail.value &&
        linkEmail.value &&
        sessionEmail.value.toLowerCase() !== linkEmail.value.toLowerCase()
      ) {
        mode.value = "conflict";
      } else {
        alreadyLoggedIn.value = true;
      }
    }
  } catch (e) {
    goError();
  } finally {
    loading.value = false;
  }
};

const writeAuthToStorage = (data) => {
  localStorage.setItem("LoggedUser", data.token);
  localStorage.setItem("IsloggIn", true);
  localStorage.setItem("username", data.user.name);
  localStorage.setItem("email", data.user.email);
  localStorage.setItem("company_id", data.user.company_id || "");
  localStorage.setItem("user_hash", data.user.user_hash || "");
  localStorage.setItem("phone_number", data.user.company?.phone_number || "");
  localStorage.setItem("company_name", data.user.company?.name || "");
  localStorage.setItem("last_name", data.user.last_name || "");
  localStorage.setItem(
    "compnay_tg_id",
    data.user.company?.trade_gecko_id || ""
  );
  localStorage.setItem("user_id", data.user.id || "");
  localStorage.setItem("last_login", data.user.last_login_at || "");
  if (data.distributor === 1) localStorage.setItem("distributor", true);

  localStorage.setItem(`login_success_${publicId}`, String(Date.now()));
};

const confirmLogin = async () => {
  btnLoading.value = true;
  errorMsg.value = "";

  try {
    const res = await axios.api.post("/auth/magic-link/confirm", {
      public_id: publicId,
      token,
      already_logged_in: alreadyLoggedIn.value,
      // include password if set
      password: newPassword.value || undefined,
    });

    if (res?.data?.status) {
      writeAuthToStorage(res.data.data);
      window.location.href = "/home";
      return;
    }
  } catch (e) {
    goError();
  } finally {
    btnLoading.value = false;
  }
};

const switchAccounts = async () => {
  btnLoading.value = true;
  errorMsg.value = "";

  try {
    localStorage.clear();
    await confirmLogin();
  } finally {
    btnLoading.value = false;
  }
};

const cancel = () => {
  window.location.href = "/home";
};

onMounted(loadPreview);
</script>
